import { useCallback, useEffect, useRef, useState } from 'react'

const MIN_LOADING_DURATION_MS = 400

export type UseCopyToClipboardParamsType = {
  value: string
  minLoadingDuration?: number
  onSuccess?: () => void
  onError?: () => void
}

/**
 * Хук для копирования текста в буфер обмена
 */
export const useCopyToClipboard = ({
  value,
  minLoadingDuration = MIN_LOADING_DURATION_MS,
  onSuccess,
  onError,
}: UseCopyToClipboardParamsType) => {
  const loadingStateRef = useRef(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleCopy = useCallback(async () => {
    if (loadingStateRef.current) return
    if (typeof navigator.clipboard === 'undefined') {
      onError?.()
      return
    }

    loadingStateRef.current = true
    setIsLoading(true)

    try {
      await navigator.clipboard.writeText(value);
      timeoutRef.current = setTimeout(() => {
        onSuccess?.()
        loadingStateRef.current = false
        setIsLoading(false)
      }, minLoadingDuration)
    } catch {
      onError?.()
      loadingStateRef.current = false
      setIsLoading(false)
      timeoutRef.current = null
    }
  }, [value, onSuccess, onError])

  return { isLoading, handleCopy }
}
